import { reactive, readonly } from 'vue'

/**
 * Reactive singleton state for the guide overlay.
 */
const guideState = reactive({
  active: false,
  steps: [],
  currentIndex: 0,
  guideId: null,
  requirementMet: false,
  wentBack: false,
  transitioning: false,
  transitionPhase: null, // 'fade-out' | 'clean' | 'fade-in' | 'entering' | null
})

/**
 * Core guide engine. Call useGuide() to get control functions.
 * Steps shape:
 * {
 *   id: string,
 *   text: string,                         // displayed on overlay
 *   attachTo?: { element: string, on?: string },  // CSS selector + placement
 *   placement?: 'top' | 'bottom' | 'left' | 'right' | 'center',
 *   canClickTarget?: boolean,
 *   advanceOn?: { selector: string, event: string },  // marks requirement met on event
 *   watchInput?: { selector: string, value: string },  // requirement: input contains value
 *   requireRoute?: string,                // requirement: route matches prefix
 *   onNext?: () => void,                  // called when Next is clicked while requirement unmet
 *   onShow?: () => void,
 *   onHide?: () => void,
 *   buttons?: Array<{ text: string, action: string, primary?: boolean }>,
 *     // action: 'next' | 'back' | 'cancel' | 'complete'
 * }
 */
export function useGuide() {
  let advanceOnCleanup = null

  function start(guideId, steps) {
    guideState.guideId = guideId
    guideState.steps = steps
    guideState.currentIndex = 0
    guideState.requirementMet = false
    guideState.active = true
    _enterStep(0)
  }

  function next() {
    if (guideState.transitioning) return
    if (guideState.currentIndex < guideState.steps.length - 1) {
      _transitionTo(guideState.currentIndex + 1, false)
    }
  }

  function back() {
    if (guideState.transitioning) return
    if (guideState.currentIndex > 0) {
      _transitionTo(guideState.currentIndex - 1, true)
    }
  }

  function _transitionTo(newIndex, isBack) {
    const oldStep = guideState.steps[guideState.currentIndex]
    const newStep = guideState.steps[newIndex]
    const bothCenter = !oldStep?.attachTo?.element && !newStep?.attachTo?.element

    _leaveStep(guideState.currentIndex)
    guideState.transitioning = true

    if (bothCenter) {
      // Center-to-center: quick crossfade, no overlay animation
      guideState.transitionPhase = 'fade-out'
      setTimeout(() => {
        guideState.currentIndex = newIndex
        guideState.requirementMet = false
        guideState.wentBack = isBack
        guideState.transitionPhase = 'entering'
        _enterStep(newIndex)
        setTimeout(() => {
          guideState.transitioning = false
          guideState.transitionPhase = null
        }, 300)
      }, 200)
      return
    }

    // Full transition: fade-out → clean → fade-in → entering
    guideState.transitionPhase = 'fade-out'
    setTimeout(() => {
      guideState.transitionPhase = 'clean'
      setTimeout(() => {
        guideState.currentIndex = newIndex
        guideState.requirementMet = false
        guideState.wentBack = isBack
        guideState.transitionPhase = 'fade-in'
        _enterStep(newIndex)
        setTimeout(() => {
          guideState.transitionPhase = 'entering'
          setTimeout(() => {
            guideState.transitioning = false
            guideState.transitionPhase = null
          }, 500)
        }, 300)
      }, 400)
    }, 300)
  }

  function cancel() {
    _leaveStep(guideState.currentIndex)
    _cleanup()
  }

  function complete() {
    _leaveStep(guideState.currentIndex)
    _markComplete(guideState.guideId)
    _cleanup()
  }

  function _enterStep(index) {
    const step = guideState.steps[index]
    if (!step) return

    // Scroll target into view
    if (step.attachTo?.element) {
      const el = document.querySelector(step.attachTo.element)
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }

    // advanceOn: listen for event on selector — marks requirement as met
    _clearAdvanceOn()
    if (step.advanceOn) {
      const { selector, event } = step.advanceOn
      const handler = () => {
        guideState.requirementMet = true
      }
      setTimeout(() => {
        const el = document.querySelector(selector)
        if (el) {
          el.addEventListener(event, handler, { once: true })
          advanceOnCleanup = () => el.removeEventListener(event, handler)
        }
      }, 100)
    }

    if (step.onShow) step.onShow()
  }

  function _leaveStep(index) {
    const step = guideState.steps[index]
    if (step?.onHide) step.onHide()
    _clearAdvanceOn()
  }

  function _clearAdvanceOn() {
    if (advanceOnCleanup) {
      advanceOnCleanup()
      advanceOnCleanup = null
    }
  }

  function _cleanup() {
    guideState.active = false
    guideState.steps = []
    guideState.currentIndex = 0
    guideState.guideId = null
    guideState.requirementMet = false
    guideState.transitioning = false
    guideState.transitionPhase = null
  }

  function _markComplete(guideId) {
    try {
      const progress = JSON.parse(localStorage.getItem('guide_progress') || '{}')
      progress[guideId] = { completed: true, completedAt: new Date().toISOString() }
      localStorage.setItem('guide_progress', JSON.stringify(progress))
    } catch {
      // ignore storage errors
    }
  }

  return {
    state: readonly(guideState),
    _rawState: guideState,
    start,
    next,
    back,
    cancel,
    complete,
  }
}
